import React from 'react'
import {useIntl} from 'react-intl'
import {useHistory} from 'react-router-dom'
import {HStack, Text, StackProps, TextProps, useMultiStyleConfig} from '@chakra-ui/react'
import {Icon, Select, HideOnDesktop, HideOnMobile} from '_uiComponents'
import {useViewport, useAnalytics, useSortUrls, useOpenDrawer} from '_customHooks'

type SortingType = {id: string; label: string}

interface SortByWrapperProps {
    sortingOptions: SortingType[]
    selectedSortingOptionLabel?: SortingType
    limit?: number
}

/**
 * @name SortByWrapper
 *
 * @component
 *
 * @description
 * This component is responsible for rendering a sorting interface
 * that allows users to select sorting options for a product listing page (PLP).
 *
 * @remarks
 * It supports both desktop and mobile views, providing a dropdown for desktop
 * and a drawer-based interface for mobile.
 *
 * @param {SortByWrapperProps} props - The props for the SortByWrapper component.
 * @param {Array} props.sortingOptions - An array of sorting options available for selection.
 * @param {Object} props.selectedSortingOptionLabel - The currently selected sorting option label.
 *
 * @returns {JSX.Element} The rendered `SortByWrapper` component.
 *
 * @example
 * ```tsx
 * const sortingOptions = [
 *   { id: 'price-asc', label: 'Price: Low to High' },
 *   { id: 'price-desc', label: 'Price: High to Low' },
 * ];
 * const selectedSortingOptionLabel = { id: 'price-asc', label: 'Price: Low to High' };
 *
 * <SortByWrapper
 *   sortingOptions={sortingOptions}
 *   selectedSortingOptionLabel={selectedSortingOptionLabel}
 * />
 * ```
 */
const SortByWrapper: React.FC<SortByWrapperProps> = ({sortingOptions, selectedSortingOptionLabel, limit}) => {
    const history = useHistory()
    const {formatMessage} = useIntl()
    const {isMobile} = useViewport()
    const {analyticsFilters} = useAnalytics()
    const styles = useMultiStyleConfig('SortByWrapper') as Record<string, StackProps | TextProps>
    const {openSortByDrawer} = useOpenDrawer()
    const sortLabel = formatMessage({id: 'sorting.plp.name', defaultMessage: 'SORT BY:'})
    const selectLabel = formatMessage({id: 'resource.select', defaultMessage: 'Select…'})
    // Add a default "Best Matches" option
    // The sorting rule is not present by default, so we add it to ensure the user can view
    // products sorted according to the catalog's sorting rule
    if (sortingOptions.length > 0 && !sortingOptions.some((option) => option.id === 'best-matches')) {
        sortingOptions.push({
            id: 'best-matches',
            label: formatMessage({id: 'sorting.plp.best_matches', defaultMessage: 'Recommended'}),
        })
    }
    const sortUrls = useSortUrls({options: sortingOptions})

    const optionsWithUrls = sortingOptions?.map((option, index) => ({
        ...option,
        value: sortUrls[index],
    }))
    const defaultValue = optionsWithUrls.find((option) => option.label === selectedSortingOptionLabel?.label)?.value

    const handleOnChange = (value) => {
        const selectedOption = optionsWithUrls.find((option) => option.value === value)

        let kindSort
        switch (selectedOption?.id) {
            case 'price-ascending':
                kindSort = 'price ascend'
                break
            case 'Online Timestamp':
                kindSort = 'new arrival'
                break
            case 'best-matches':
                kindSort = 'best matches'
                break
            default:
                kindSort = 'price descend'
        }

        analyticsFilters({action: 'sort', label: kindSort, type: 'sort', value: kindSort})
        if (value) {
            const url = new URL(value, window.location.origin)
            const searchParams = url.searchParams

            if (searchParams.has('start')) {
                searchParams.set('start', '0')
            }
            if (searchParams.has('sz')) {
                searchParams.set('sz', typeof limit === 'number' && limit > 0 ? String(limit) : '20')
            }

            url.search = searchParams.toString()

            history.replace(url.pathname + url.search, {
                position: (history.location.state as {position?: number})?.position,
            })
        }
    }

    const openMobileSortBy = () => {
        openSortByDrawer({
            sortLabel: sortLabel,
            optionsWithUrls: optionsWithUrls,
            defaultValue: defaultValue,
            handleOnChange: handleOnChange,
        })
    }

    return (
        <>
            <HStack as={isMobile ? 'button' : HStack} {...styles.wrapper} onClick={() => isMobile && openMobileSortBy()} data-testid="sort-by-wrapper">
                <Text>{sortLabel}</Text>
                <HideOnDesktop>
                    <Icon name="IconSorting" size="sm" />
                </HideOnDesktop>
                <HideOnMobile>
                    <Select
                        variant="sortBy"
                        name="sort-by"
                        label={sortLabel}
                        aria-label={sortLabel}
                        placeholder={selectLabel}
                        options={optionsWithUrls}
                        defaultValue={defaultValue}
                        onChange={(value: string | null) => {
                            handleOnChange(value)
                        }}
                    />
                </HideOnMobile>
            </HStack>
        </>
    )
}

export {SortByWrapper}
